import { useMemo, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { Line } from "react-chartjs-2";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { Draggable } from "@hello-pangea/dnd";
import { useGetDataQuery } from "features/apiSlice";
import colors from "assets/theme/base/colors";
import Dropdown from "../../../Dropdown";
import configs from "./configs";
import chartsData from "../../../data/chartData";
import PopUp from "../../../PopUp";
import { LINECHART_DROPDOWN_LIST } from "../../../configs/dropdownConfigs";
import { LINECHART_AXIS_STEP_SIZE } from "../../../configs/chartConfigs";
import ContextMenu from "../../ContextMenu";

function Linechart({
  setLayoutData,
  widgetHeight,
  widget,
  cellWidgetInfo,
  index,
  masterContainerId,
  cellId,
  handleDeleteWidget,
  containerType,
  handleBreadcrumbs,
  isEditMode,
  isAdmin,
}) {
  let getDataURL = null;
  let refreshInterval = 0;
  let actions = [];
  let thresholdLower = null;
  let thresholdUpper = null;
  if (widget) {
    getDataURL = widget.getDataURL;
    refreshInterval = widget.refreshInterval ? widget.refreshInterval : 0;
    actions = widget.actions ? widget.actions : [];
    thresholdLower = widget.thresholdLower;
    thresholdUpper = widget.thresholdUpper;
  }

  const linechartQuery = getDataURL
    ? useGetDataQuery(getDataURL, {
        pollingInterval: refreshInterval,
        refetchOnReconnect: true,
      })
    : {};

  const [popUpOpen, setPopUpOpen] = useState(false);
  const [popUpDashboardId, setPopUpDashboardId] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);

  const handleContextMenu = (event) => {
    event.preventDefault();
    setContextMenu(
      contextMenu === null
        ? {
            mouseX: event.clientX + 2,
            mouseY: event.clientY - 6,
          }
        : null,
    );
  };

  const handleClose = () => setContextMenu(null);

  const linechartLabels = [];
  const linechartDatasets = [];

  try {
    if (linechartQuery.data)
      linechartQuery.data.forEach((dataPoint, index) => {
        linechartDatasets.push(dataPoint[widget.valueField]);
        if (
          widget.legendField === "eventEpoch" ||
          widget.valueFieldDataType === "epoch"
        ) {
          if (index % LINECHART_AXIS_STEP_SIZE === 0) {
            const event = new Date(dataPoint[widget.legendField] * 1000);
            const epochTime = event.toTimeString().substring(0, 8);
            linechartLabels.push(epochTime);
          } else linechartLabels.push("");
        } else {
          linechartLabels.push(dataPoint[widget.legendField]);
        }
      });
  } catch (error) {
    console.error("Error in Linechart: ", error);
  }

  let datasets = [
    {
      label: linechartQuery.data ? "" : "Dummy-data-label",
      color: "info",
      data: linechartQuery.data ? linechartDatasets : chartsData.datasets,
    },
  ];
  if (thresholdLower && thresholdUpper)
    datasets = [
      ...datasets,
      {
        label: linechartQuery.data ? "Low" : "Dummy-lower-threshold",
        color: "primary",
        data: Array(
          linechartQuery.data
            ? linechartDatasets.length
            : chartsData.datasets.length,
        ).fill(thresholdLower, 0),
      },
      {
        label: linechartQuery.data ? "High" : "Dummy-upper-threshold",
        color: "success",
        data: Array(
          linechartQuery.data
            ? linechartDatasets.length
            : chartsData.datasets.length,
        ).fill(thresholdUpper, 0),
      },
    ];
  else if (thresholdLower)
    datasets = [
      ...datasets,
      {
        label: linechartQuery.data ? "Low" : "Dummy-lower-threshold",
        color: "primary",
        data: Array(
          linechartQuery.data
            ? linechartDatasets.length
            : chartsData.datasets.length,
        ).fill(thresholdLower, 0),
      },
    ];
  else if (thresholdUpper)
    datasets = [
      ...datasets,
      {
        label: linechartQuery.data ? "High" : "Dummy-upper-threshold",
        color: "success",
        data: Array(
          linechartQuery.data
            ? linechartDatasets.length
            : chartsData.datasets.length,
        ).fill(thresholdUpper, 0),
      },
    ];

  const lineChartData = {
    labels: linechartQuery.data ? linechartLabels : chartsData.labels,
    datasets,
  };

  const chartData = {
    labels: lineChartData.labels,
    data: lineChartData.datasets
      ? lineChartData.datasets.map((dataset) => ({
          ...dataset,
          tension: 0,
          pointRadius: 1,
          borderWidth: 2,
          backgroundColor: "transparent",
          fill: true,
          pointBackgroundColor: colors[dataset.color]
            ? colors[dataset.color || "dark"].main
            : colors.dark.main,
          borderColor: colors[dataset.color]
            ? colors[dataset.color || "dark"].main
            : colors.dark.main,
          maxBarThickness: 6,
        }))
      : [],
  };

  const { data, options } = configs(
    chartData.labels,
    chartData.data,
    actions,
    setPopUpOpen,
    setPopUpDashboardId,
    handleBreadcrumbs,
  );

  const draggableId = `${cellWidgetInfo.widgetType}/////${
    cellWidgetInfo.id
  }/////${uuidv4()}`;

  let primaryLabel = null;
  let secondaryLabel = null;
  let chart = null;
  let dropdownOptions = isAdmin
    ? LINECHART_DROPDOWN_LIST
    : LINECHART_DROPDOWN_LIST.slice(0, -1);
  let error = null;
  if (widget) {
    if (linechartQuery.data) {
      primaryLabel = widget.primaryLabel ? widget.primaryLabel : "";
      secondaryLabel = widget.secondaryLabel ? widget.secondaryLabel : "";
      chart = (
        <MDBox height={widgetHeight}>
          <Line data={data} options={options} />
          <PopUp
            isAdmin={isAdmin}
            open={popUpOpen}
            handleClose={() => setPopUpOpen(false)}
            dashboardId={popUpDashboardId}
          />
        </MDBox>
      );
    } else {
      primaryLabel = "Unable to update the widget";
      secondaryLabel = "";
      chart = "";
      dropdownOptions = isAdmin
        ? LINECHART_DROPDOWN_LIST.slice(LINECHART_DROPDOWN_LIST.length - 1)
        : [];
      error = (
        <Icon
          sx={{ marginLeft: "auto", marginTop: ".3em", color: "red" }}
          fontSize="medium"
        >
          warningambericon
        </Icon>
      );
    }
  } else {
    primaryLabel = "Dummy Primary Label";
    secondaryLabel = "Widget ID not configured";
    chart = (
      <MDBox height={widgetHeight}>
        <Line data={data} options={options} />
      </MDBox>
    );
    error = (
      <Icon
        sx={{ marginLeft: "auto", marginTop: ".3em", color: "red" }}
        fontSize="medium"
      >
        warningambericon
      </Icon>
    );
  }

  const renderChart = (
    <MDBox p={2} position="relative">
      <MDBox
        width="2.5rem"
        height="2.5rem"
        bgColor="info"
        variant="gradient"
        coloredShadow="info"
        borderRadius="lg"
        display="flex"
        justifyContent="center"
        alignItems="center"
        color="white"
        position="absolute"
        top="-.7em"
      >
        <Icon fontSize="small">insights</Icon>
      </MDBox>
      <MDBox display="flex" justifyContent="space-between" width="100%" mb={2}>
        <MDBox ml={6}>
          <MDTypography variant="h6">{primaryLabel}</MDTypography>
          <MDTypography component="div" variant="button" color="text">
            {secondaryLabel}
          </MDTypography>
        </MDBox>
        {error}
        {isEditMode && dropdownOptions.length > 0 && (
          <Dropdown
            handleDeleteWidget={handleDeleteWidget}
            masterContainerId={masterContainerId}
            cellId={cellId}
            index={index}
            setLayoutData={setLayoutData}
            options={dropdownOptions}
          />
        )}
      </MDBox>
      {useMemo(() => chart, [linechartQuery.data, popUpOpen])}
    </MDBox>
  );

  const pdfExportId = `${primaryLabel}/${cellWidgetInfo.id}`;

  return isEditMode ? (
    <Draggable draggableId={draggableId} index={index}>
      {(provided) => (
        <Card
          sx={{
            flexGrow: "1",
            flexBasis: "0",
            margin: ".5em",
            marginTop:
              index > 0 && containerType === "vertical" ? "1em" : "0.5em",
          }}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          ref={provided.innerRef}
        >
          {renderChart}
        </Card>
      )}
    </Draggable>
  ) : (
    <Card
      sx={{
        flexGrow: "1",
        flexBasis: "0",
        margin: ".5em",
        marginTop: index > 0 && containerType === "vertical" ? "1em" : "0.5em",
      }}
      onContextMenu={handleContextMenu}
      id={pdfExportId}
    >
      {renderChart}
      {widget && (
        <ContextMenu
          id={pdfExportId}
          contextMenu={contextMenu}
          handleClose={handleClose}
          name={primaryLabel}
        />
      )}
    </Card>
  );
}

export default Linechart;
